// src/Pages/Home/NapkinHomePage.tsx
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { GoogleOAuthProvider } from "@react-oauth/google";
import "../../Styles/HomePageStyles/Home.css";
import { useUser } from "../../Context/UserContext";
import LogoWithName from "../../Assets/Images/napkin-logo.png";
import NapkinMark from "../../Assets/Images/napkin-mark.png";
import LoginModal from "../UserAuthComponents/LoginModal";
import SignupModal from "../UserAuthComponents/SignupModal";

const GOOGLE_CLIENT_ID =
  "1029580329318-me6qc1rpahlr5bucs851e60p8p60f24c.apps.googleusercontent.com";

type UserProfile = {
  name: string;
  email: string;
  loginMethod: string;
} | null;

/**
 * NapkinHomePage
 * - Modern, responsive homepage modeled after the TeacherHomePage layout.
 * - Uses page-specific classnames (napkin-*) to avoid collisions.
 */
const NapkinHomePage: React.FC = () => {
  const [profile, setProfile] = useState<UserProfile>(null);
  const { user, loading, fetchUserProfile } = useUser();

  const [searchParams, setSearchParams] = useSearchParams();
  const activeModal = searchParams.get("modal");
  const prefillEmail = searchParams.get("email") || "";

  const openModal = (which: "login" | "signup", email?: string) => {
    const params: Record<string, string> = { modal: which };
    if (email) params.email = email;
    setSearchParams(params, { replace: true });
  };
  const closeModal = () => setSearchParams({}, { replace: true });

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // When auth is nuked, also clear any stale profile so "Hi, Matthew" disappears
  useEffect(() => {
    if (!user) setProfile(null);
  }, [user]);

  // When we have a user and we're not loading, fetch a fresh profile
  useEffect(() => {
    if (!loading && user) {
      (async () => {
        const p = await fetchUserProfile();
        if (p) setProfile(p);
        else setProfile(null);
      })();
    }
  }, [user, loading, fetchUserProfile]);

  // Lock body scroll when a modal is open
  useEffect(() => {
    if (activeModal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [activeModal]);

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    const emailInput = (e.currentTarget.elements.namedItem("email") ??
      null) as HTMLInputElement | null;
    const email = emailInput?.value?.trim();
    if (email) {
      openModal("signup", email);
    }
  };

  return (
    <div className="napkin-home">
      {/* NAV */}
      <nav className="napkin-nav">
        <div className="napkin-container napkin-nav-inner">
          <a className="napkin-brand" href="/" aria-label="Napkin Home">
            <img
              className="navbar-logo"
              src={LogoWithName}
              alt="Napkin"
            />
          </a>
          <div className="napkin-nav-cta">
            {/* Greet only when truly authenticated */}
            {user && profile?.name && (
              <span className="napkin-welcome">
                Hi, {profile.name.split(" ")[0]}!
              </span>
            )}

            {/* If authenticated (profile present), show Go to app. Otherwise, Log in / Get started */}
            {loading ? null : profile ? (
              <a className="napkin-btn napkin-btn-primary" href="/app">
                Go to app
              </a>
            ) : (
              <>
                <button
                  type="button"
                  className="napkin-btn napkin-btn-ghost"
                  onClick={() => openModal("login")}
                >
                  Log in
                </button>
                <button
                  type="button"
                  className="napkin-btn napkin-btn-primary"
                  onClick={() => openModal("signup")}
                >
                  Get started
                </button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* HERO */}
      <header className="napkin-hero" id="get-started">
        <div className="napkin-container napkin-hero-inner">
          <div className="napkin-hero-copy">
            {/* <p className="napkin-pill">Napkin · Simple contracts, real signatures</p> */}
            <h1>Legal contracts made simple.</h1>
            <p className="napkin-sub">
              Plain-English agreements that are fast, binding, and easy to
              manage. Keep your deals moving with agreements designed to be
              signed, not ignored.
            </p>

            <form className="napkin-hero-cta" onSubmit={handleSubmit}>
              <div className="napkin-input-wrap">
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="name@company.com"
                  aria-label="Email address"
                />
                <button className="napkin-btn napkin-btn-primary" type="submit">
                  Get started free
                </button>
              </div>
            </form>

            <p className="napkin-tiny napkin-terms">
              By clicking Get started, you agree to our{" "}
              <a href="/terms" target="_blank" rel="noreferrer">
                Terms
              </a>{" "}
              and{" "}
              <a href="/privacy" target="_blank" rel="noreferrer">
                Privacy Policy
              </a>
              .
            </p>

            <ul className="napkin-hero-checks">
              <li>
                <img
                  src="https://napkin-app.s3.us-east-2.amazonaws.com/blue-check.png"
                  alt=""
                />
                Close more deals with less legalese.
              </li>
              <li>
                <img
                  src="https://napkin-app.s3.us-east-2.amazonaws.com/blue-check.png"
                  alt=""
                />
                Less friction. More signatures.
              </li>
              <li>
                <img
                  src="https://napkin-app.s3.us-east-2.amazonaws.com/blue-check.png"
                  alt=""
                />
                Agreements that don't kill the vibe.
              </li>
            </ul>
          </div>

          <div className="napkin-hero-art" aria-hidden="true">
            <div className="napkin-orb napkin-orb-a" />
            <div className="napkin-orb napkin-orb-b" />
            <div className="napkin-card-hero">
              <img
                className="napkin-badge"
                src={NapkinMark}
                alt="Napkin badge"
              />
              <div>
                <h3>Legally binding, human friendly</h3>
                <p>
                  Every agreement is readable, reviewable, and backed by
                  e-signature records.
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="napkin-wave" />
      </header>

      {/* FEATURES */}
      <section className="napkin-section napkin-section-alt">
        <div className="napkin-container">
          <h2>Everything you need to close deals faster</h2>
          <p className="napkin-section-sub">
            From template to signature in minutes, not days.
          </p>
          <div className="napkin-grid napkin-features">
            {/* Hero features — top row */}
            <div className="napkin-feat napkin-feat-hero">
              <div className="napkin-feat-icon" aria-hidden="true">
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                  <rect x="8" y="4" width="28" height="36" rx="4" fill="#e2e8f0" stroke="#94a3b8" strokeWidth="1.5"/>
                  <rect x="14" y="12" width="16" height="2" rx="1" fill="#cbd5e1"/>
                  <rect x="14" y="18" width="12" height="2" rx="1" fill="#cbd5e1"/>
                  <rect x="14" y="24" width="14" height="2" rx="1" fill="#cbd5e1"/>
                  <rect x="14" y="30" width="10" height="2" rx="1" fill="#cbd5e1"/>
                  <path d="M38 8L40 6L42 8L40 10Z" fill="#395bf6"/>
                  <path d="M40 4L40 12M36 8L44 8" stroke="#395bf6" strokeWidth="1" strokeLinecap="round" opacity="0.4"/>
                </svg>
              </div>
              <div>
                <h3>Start from a template</h3>
                <p>
                  Pick a ready-made contract, customize the details, and send it
                  in minutes.
                </p>
              </div>
            </div>
            <div className="napkin-feat napkin-feat-hero">
              <div className="napkin-feat-icon" aria-hidden="true">
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                  <rect x="6" y="16" width="16" height="12" rx="6" fill="none" stroke="#94a3b8" strokeWidth="2"/>
                  <rect x="18" y="16" width="16" height="12" rx="6" fill="none" stroke="#94a3b8" strokeWidth="2"/>
                  <line x1="14" y1="22" x2="26" y2="22" stroke="#cbd5e1" strokeWidth="1.5"/>
                  <circle cx="38" cy="36" r="8" fill="#e2e8f0" stroke="#395bf6" strokeWidth="1.5"/>
                  <path d="M34 36L37 39L42 33" stroke="#395bf6" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div>
                <h3>Share a link, get a signature</h3>
                <p>
                  Send a secure link. Your client signs from any device — no
                  app, no account.
                </p>
              </div>
            </div>

            {/* Supporting features — bottom row */}
            <div className="napkin-feat napkin-feat-support">
              <div className="napkin-feat-icon" aria-hidden="true">
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                  <path d="M16 3L27 8V16C27 22 22 27 16 29C10 27 5 22 5 16V8L16 3Z" fill="#e2e8f0" stroke="#94a3b8" strokeWidth="1.5" strokeLinejoin="round"/>
                  <path d="M11 16L14 19L21 12" stroke="#395bf6" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>Legally binding by default</h3>
              <p>Every signature is time-stamped with a tamper-proof audit trail.</p>
            </div>
            <div className="napkin-feat napkin-feat-support">
              <div className="napkin-feat-icon" aria-hidden="true">
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                  <path d="M4 8H12L14 6H26V24H4Z" fill="#e2e8f0" stroke="#94a3b8" strokeWidth="1.5" strokeLinejoin="round"/>
                  <rect x="4" y="10" width="22" height="14" rx="2" fill="#fff" stroke="#94a3b8" strokeWidth="1.5"/>
                  <circle cx="22" cy="22" r="5" fill="none" stroke="#395bf6" strokeWidth="1.5"/>
                  <line x1="26" y1="26" x2="29" y2="29" stroke="#395bf6" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <h3>Find any agreement instantly</h3>
              <p>All your signed contracts in one searchable archive.</p>
            </div>
            <div className="napkin-feat napkin-feat-support">
              <div className="napkin-feat-icon" aria-hidden="true">
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                  <circle cx="12" cy="10" r="4" fill="#e2e8f0" stroke="#94a3b8" strokeWidth="1.5"/>
                  <path d="M4 26C4 20 8 17 12 17C16 17 20 20 20 26" stroke="#94a3b8" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
                  <circle cx="20" cy="10" r="4" fill="#e2e8f0" stroke="#395bf6" strokeWidth="1.5"/>
                  <path d="M14 26C14 20 17 17 20 17C23 17 28 20 28 26" stroke="#395bf6" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
                </svg>
              </div>
              <h3>Built for your whole team</h3>
              <p>Add team members, assign roles, and see who signed what.</p>
            </div>
            <div className="napkin-feat napkin-feat-support">
              <div className="napkin-feat-icon" aria-hidden="true">
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                  <rect x="4" y="4" width="10" height="10" rx="3" fill="#e2e8f0" stroke="#94a3b8" strokeWidth="1.5"/>
                  <rect x="18" y="4" width="10" height="10" rx="3" fill="#e2e8f0" stroke="#94a3b8" strokeWidth="1.5"/>
                  <rect x="4" y="18" width="10" height="10" rx="3" fill="#e2e8f0" stroke="#94a3b8" strokeWidth="1.5"/>
                  <rect x="18" y="18" width="10" height="10" rx="3" fill="#e2e8f0" stroke="#395bf6" strokeWidth="1.5"/>
                  <line x1="14" y1="9" x2="18" y2="9" stroke="#cbd5e1" strokeWidth="1.5" strokeLinecap="round"/>
                  <line x1="14" y1="23" x2="18" y2="23" stroke="#cbd5e1" strokeWidth="1.5" strokeLinecap="round"/>
                  <line x1="9" y1="14" x2="9" y2="18" stroke="#cbd5e1" strokeWidth="1.5" strokeLinecap="round"/>
                  <line x1="23" y1="14" x2="23" y2="18" stroke="#cbd5e1" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </div>
              <h3>Connects to your stack</h3>
              <p>Works with the email and storage tools you already use.</p>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="napkin-section">
        <div className="napkin-container">
          <h2>Three steps. That's it.</h2>
          <p className="napkin-section-sub">
            No training, no onboarding calls. Just contracts that get signed.
          </p>
          <div className="napkin-steps">
            <div className="napkin-step">
              <div className="napkin-step-num" aria-hidden="true">1</div>
              <h3>Pick a template</h3>
              <p>Choose from ready-made contracts or start from scratch.</p>
            </div>
            <div className="napkin-step">
              <div className="napkin-step-num" aria-hidden="true">2</div>
              <h3>Add signers</h3>
              <p>Enter their email. They get a secure link to sign.</p>
            </div>
            <div className="napkin-step">
              <div className="napkin-step-num" aria-hidden="true">3</div>
              <h3>Done. Stored.</h3>
              <p>Signed copies are filed and searchable instantly.</p>
            </div>
          </div>

          {/* Trust bar */}
          <div className="napkin-trust">
            <div className="napkin-trust-item">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <rect x="3" y="9" width="14" height="9" rx="2" fill="none" stroke="#94a3b8" strokeWidth="1.5"/>
                <path d="M6 9V6a4 4 0 018 0v3" fill="none" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round"/>
                <circle cx="10" cy="13.5" r="1.5" fill="#395bf6"/>
              </svg>
              256-bit encryption
            </div>
            <div className="napkin-trust-item">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <path d="M10 2L17 5.5V10C17 14 14 17 10 18.5C6 17 3 14 3 10V5.5L10 2Z" fill="none" stroke="#94a3b8" strokeWidth="1.5" strokeLinejoin="round"/>
                <path d="M7 10.5L9 12.5L13 8.5" stroke="#395bf6" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              ESIGN {"&"} UETA compliant
            </div>
            <div className="napkin-trust-item">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <circle cx="10" cy="10" r="4" fill="#22c55e"/>
                <circle cx="10" cy="10" r="7" fill="none" stroke="#22c55e" strokeWidth="1" opacity="0.3"/>
              </svg>
              99.9% uptime
            </div>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="napkin-section napkin-section-alt" id="pricing">
        <div className="napkin-container">
          <h2 className="napkin-section-center">Simple pricing for every team</h2>
          <p className="napkin-section-sub napkin-section-center">
            Start free. Upgrade when you're ready.
          </p>
          <div className="napkin-pricing-grid">
            {/* Free */}
            <div className="napkin-price-card">
              <div className="napkin-price-header">
                <span className="napkin-price-name">Free</span>
                <span className="napkin-price-amount">$0</span>
                <span className="napkin-price-period">Free forever</span>
              </div>
              <div className="napkin-price-divider" />
              <ul className="napkin-price-features">
                <li>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M3.5 8.5L6.5 11.5L12.5 5" stroke="#395bf6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  3 signature requests/mo
                </li>
                <li>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M3.5 8.5L6.5 11.5L12.5 5" stroke="#395bf6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  5 templates
                </li>
                <li>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M3.5 8.5L6.5 11.5L12.5 5" stroke="#395bf6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  1 user
                </li>
                <li>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M3.5 8.5L6.5 11.5L12.5 5" stroke="#395bf6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  Basic audit trail
                </li>
              </ul>
              <button
                type="button"
                className="napkin-btn napkin-btn-ghost napkin-price-cta"
                onClick={() => openModal("signup")}
              >
                Get started
              </button>
            </div>

            {/* Pro — featured */}
            <div className="napkin-price-card napkin-price-card--featured">
              <span className="napkin-price-badge">Most popular</span>
              <div className="napkin-price-header">
                <span className="napkin-price-name">Pro</span>
                <span className="napkin-price-amount">
                  $7.99<span className="napkin-price-mo">/mo</span>
                </span>
                <span className="napkin-price-period">Billed annually, or $12.99/mo</span>
              </div>
              <div className="napkin-price-divider" />
              <ul className="napkin-price-features">
                <li>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M3.5 8.5L6.5 11.5L12.5 5" stroke="#395bf6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  Unlimited signatures
                </li>
                <li>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M3.5 8.5L6.5 11.5L12.5 5" stroke="#395bf6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  All templates
                </li>
                <li>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M3.5 8.5L6.5 11.5L12.5 5" stroke="#395bf6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  Quick Send
                </li>
                <li>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M3.5 8.5L6.5 11.5L12.5 5" stroke="#395bf6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  Full audit trail
                </li>
                <li>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M3.5 8.5L6.5 11.5L12.5 5" stroke="#395bf6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  Email support
                </li>
              </ul>
              <button
                type="button"
                className="napkin-btn napkin-btn-primary napkin-price-cta"
                onClick={() => openModal("signup")}
              >
                Start free trial
              </button>
            </div>

            {/* Business */}
            <div className="napkin-price-card">
              <div className="napkin-price-header">
                <span className="napkin-price-name">Business</span>
                <span className="napkin-price-amount">
                  $19.99<span className="napkin-price-mo">/mo</span>
                </span>
                <span className="napkin-price-period">Billed annually, or $29.99/mo</span>
              </div>
              <div className="napkin-price-divider" />
              <ul className="napkin-price-features">
                <li>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M3.5 8.5L6.5 11.5L12.5 5" stroke="#395bf6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  Everything in Pro
                </li>
                <li>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M3.5 8.5L6.5 11.5L12.5 5" stroke="#395bf6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  AI-powered features
                </li>
                <li>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M3.5 8.5L6.5 11.5L12.5 5" stroke="#395bf6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  Unlimited Quick Send templates
                </li>
                <li>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M3.5 8.5L6.5 11.5L12.5 5" stroke="#395bf6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  Up to 10 users
                </li>
                <li>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M3.5 8.5L6.5 11.5L12.5 5" stroke="#395bf6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  Priority support
                </li>
              </ul>
              <button
                type="button"
                className="napkin-btn napkin-btn-primary napkin-price-cta"
                onClick={() => openModal("signup")}
              >
                Start free trial
              </button>
            </div>

            {/* Enterprise */}
            <div className="napkin-price-card">
              <div className="napkin-price-header">
                <span className="napkin-price-name">Enterprise</span>
                <span className="napkin-price-amount">Custom</span>
                <span className="napkin-price-period">Tailored to your needs</span>
              </div>
              <div className="napkin-price-divider" />
              <ul className="napkin-price-features">
                <li>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M3.5 8.5L6.5 11.5L12.5 5" stroke="#395bf6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  Everything in Business
                </li>
                <li>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M3.5 8.5L6.5 11.5L12.5 5" stroke="#395bf6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  Unlimited users
                </li>
                <li>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M3.5 8.5L6.5 11.5L12.5 5" stroke="#395bf6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  Custom integrations
                </li>
                <li>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M3.5 8.5L6.5 11.5L12.5 5" stroke="#395bf6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  Dedicated account manager
                </li>
                <li>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M3.5 8.5L6.5 11.5L12.5 5" stroke="#395bf6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  SLA {"&"} uptime guarantee
                </li>
              </ul>
              <a className="napkin-btn napkin-btn-ghost napkin-price-cta" href="mailto:contact@napkinsend.com">
                Contact us
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="napkin-cta">
        <div className="napkin-container">
          <h2>Your next agreement is 5 minutes away</h2>
          <p className="napkin-sub" style={{ textAlign: "center", margin: "0 auto 28px" }}>
            Start free. No credit card. Cancel anytime.
          </p>
          <form className="napkin-hero-cta" onSubmit={handleSubmit} style={{ alignItems: "center" }}>
            <div className="napkin-input-wrap" style={{ margin: "0 auto" }}>
              <input
                type="email"
                name="email"
                required
                placeholder="name@company.com"
                aria-label="Email address"
              />
              <button className="napkin-btn napkin-btn-primary" type="submit">
                Create your first contract
              </button>
            </div>
          </form>
          <p className="napkin-tiny napkin-terms" style={{ textAlign: "center", marginTop: 8 }}>
            Free plan includes 3 signature requests/month.{" "}
            <a href="/terms" target="_blank" rel="noreferrer">Terms</a> {"&"}{" "}
            <a href="/privacy" target="_blank" rel="noreferrer">Privacy</a>.
          </p>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="napkin-footer">
        <div className="napkin-container">
          <p>
            {"©"} {new Date().getFullYear()} Napkin {" · "}
            <a href="/terms">Terms</a> {" · "}
            <a href="/privacy">Privacy</a>
          </p>
        </div>
      </footer>

      {/* AUTH MODALS — Google OAuth loads only when a modal is open */}
      {activeModal && (
        <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
          <LoginModal
            isOpen={activeModal === "login"}
            onClose={closeModal}
            onSwitchToSignup={() => openModal("signup")}
          />
          <SignupModal
            isOpen={activeModal === "signup"}
            onClose={closeModal}
            onSwitchToLogin={() => openModal("login")}
            initialEmail={prefillEmail}
          />
        </GoogleOAuthProvider>
      )}
    </div>
  );
};

export default NapkinHomePage;
